import {window} from '@acng/frontend-bounty/dom/window.js';
import {service, ServiceWorker, click, StorageManager, BasicService} from 'nova';
import {DropdownController, ClickEventController} from 'nova';
import {permission} from '../core/permissions.js';
import {ObrienClient} from '../api/obrien-client.js';

/**
 * @extends {BasicService<'notifications', null>}
 */
@service
class NotificationManager extends BasicService {
  #STORAGE_KEY = 'notification-requested';
  #serviceWorker = new ServiceWorker();
  #session = new StorageManager().session;
  #obrien = new ObrienClient();

  /** @type {PushManager | undefined} */
  #manager;

  constructor() {
    super('notifications');
  }

  /**
   * @returns {Promise<PushManager>}
   *
   * @override
   */
  async setup() {
    if (this.#manager) {
      return this.#manager;
    }

    const registration = await this.#serviceWorker.setup();
    const {pushManager} = registration;

    this.#manager = pushManager;

    return pushManager;
  }

  @click async subscribe() {
    const pushManager = await this.setup();
    const vapid = await this.#obrien.vapid;

    /**
     * "Polyfill" for Uint8Array.fromBase64
     *
     * @param {string} base64String
     *
     * @returns {Uint8Array}
     */
    const urlBase64ToUint8Array = (base64String) => {
      const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
      const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);

      return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
    };

    const sub = await pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid),
    });

    try {
      await this.#obrien.subscribe(sub, vapid);
    } catch (e) {
      await sub.unsubscribe();

      throw e;
    }

    return sub;
  }

  @click async unsubscribe() {
    const pushManager = await this.setup();
    const sub = await pushManager.getSubscription();
    this.#session[this.#STORAGE_KEY] = true;

    if (!sub || !(await sub.unsubscribe())) {
      throw Error('Cannot unsubscribe', {cause: sub});
    }

    await this.#obrien.unsubscribe(sub);
  }

  async #getSubscription() {
    const pushManager = await this.setup();
    let subscription = await pushManager.getSubscription();

    if (!subscription && (await permission.notifications.granted())) {
      subscription = await this.subscribe();
    }

    return subscription;
  }

  /**
   * @returns {Promise<DropdownController | null>}
   */
  async confirm() {
    if (this.#session[this.#STORAGE_KEY]) {
      DEBUG: this.reason.info("Already asked.")
      return null;
    }

    if (await this.#getSubscription()) {
      DEBUG: this.reason.info("Already subscribed.")
      return null;
    }

    if (await permission.notifications.denied()) {
      DEBUG: this.reason.info("Permission denied.")
      return null;
    }

    DEBUG: this.reason.log("Create confirm popup.");
    return new DropdownController((close) => ({
      subscribe: new ClickEventController(() => {
        close();
        this.subscribe();
      }),
      close: new ClickEventController(() => {
        close();
        this.#session[this.#STORAGE_KEY] = true;
      }),
    }));
  }
}

export {NotificationManager};
